import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import { MergerSetupPanel } from "../../../../../mergers/mergerSetup";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import {
  CommentDto,
  CreditRiskLifecycle,
  TradingCounterParty
} from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { WarehouseSyncDialog } from "../../../../../../components/lifecycle/warehouseSync";
import {
  creditRiskChanges,
  mirroredSanctionScreening,
  patchTouchesCreditRiskData,
  syncedCreditRiskProgress
} from "../../../../../../components/lifecycle/warehouseSync/applyPatch";
import { HubSyncValues } from "../../../../../../components/lifecycle/warehouseSync/utils";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  kycCheck: string;
  kycCheckAssignee: string;
  sanctionScreening: string;
  kycCheckMft: string;
  kycCheckMftAssignee: string;
  creditCheck: string;
  creditCheckAssignee: string;
  creditAmount: number | undefined;
  creditCurrency: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const Compliance: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "compliance"
  );

  const [formData, setFormData] = useState<FormData>({
    kycCheck: specificCounterparty?.complianceLifecycle?.kycCheck || "",
    kycCheckAssignee:
      specificCounterparty?.complianceLifecycle?.kycCheckAssignee || "",
    sanctionScreening:
      specificCounterparty?.complianceLifecycle?.sanctionScreening || "",
    kycCheckMft: specificCounterparty?.complianceLifecycle?.kycCheckMft || "",
    kycCheckMftAssignee:
      specificCounterparty?.complianceLifecycle?.kycCheckMftAssignee || "",
    creditCheck: specificCounterparty?.creditRiskLifecycle?.creditCheck || "",
    creditCheckAssignee:
      specificCounterparty?.creditRiskLifecycle?.creditCheckAssignee || "",
    creditAmount:
      specificCounterparty?.creditRiskLifecycle?.creditAmount ?? undefined,
    creditCurrency:
      specificCounterparty?.creditRiskLifecycle?.creditCurrency || "",
    comments: specificCounterparty?.complianceLifecycle?.comments || [],
    signOffNote: specificCounterparty?.complianceLifecycle?.signOffNote || ""
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  // ADHO-490. The warehouse link lives on the counterparty root rather than inside a lifecycle,
  // because Compliance and Risk both read it. Accepting a sync only stages the values here; they
  // are written on Save or Complete like every other field in this dialog.
  const [syncOpen, setSyncOpen] = useState(false);
  const [catalystId, setCatalystId] = useState(
    specificCounterparty?.creditCatalystId || ""
  );
  const [syncedAt, setSyncedAt] = useState(
    specificCounterparty?.creditCatalystSyncedAt || ""
  );
  // The credit values a sync filled in, staged until Save or Complete.
  const [syncedCredit, setSyncedCredit] = useState<
    Partial<CreditRiskLifecycle>
  >({});
  const creditRiskDataSynced = Object.keys(syncedCredit).length > 0;

  const syncedRootFields = {
    creditCatalystId: catalystId || undefined,
    creditCatalystSyncedAt: syncedAt || undefined
  };

  // The credit values are stored on creditRiskLifecycle, not here: this dialog only mirrors them as
  // disabled fields. Synced values therefore have to be written onto the credit risk slice, or they
  // land in complianceLifecycle where nothing reads them and are gone on the next reload.
  //
  // Only the values a sync actually changed. Saving the values this dialog loaded would revert
  // whatever Credit Risk set while the dialog sat open, on fields Compliance cannot edit.
  const syncedCreditRiskLifecycle = creditRiskDataSynced
    ? {
        creditRiskLifecycle: {
          ...specificCounterparty.creditRiskLifecycle,
          ...syncedCredit
        }
      }
    : {};

  // A sync that fills in Credit Risk's data moves Credit Risk's progress with it. Only progress,
  // never creditRiskLifecycle.complete. See syncedCreditRiskProgress.
  const syncedCreditRiskProgressFields = creditRiskDataSynced
    ? { creditRisk: syncedCreditRiskProgress() }
    : {};

  // Same reason in reverse: keep the credit risk fields out of the compliance slice so a save does
  // not leave a dead copy there that disagrees with the value Credit Risk owns.
  const complianceFormFields = () => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCheck,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCheckAssignee,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditAmount,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCurrency,
      ...fields
    } = formData;
    return fields;
  };

  const hubSyncValues: HubSyncValues = {
    kycCheck: formData.kycCheck,
    creditCheck: formData.creditCheck,
    creditAmount: formData.creditAmount,
    creditCurrency: formData.creditCurrency
  };

  // Approved with no amount means no cap, which is how Credit Risk records an unlimited counterparty and
  // how the warehouse stores it (M_CreditLimit NULL).
  const creditIsUnlimited =
    formData.creditCheck === "approved" && formData.creditAmount === undefined;

  const handleApplySync = (patch: Partial<HubSyncValues>) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
      ...mirroredSanctionScreening(patch)
    }));
    if (patchTouchesCreditRiskData(patch)) {
      setSyncedCredit((prev) => ({ ...prev, ...creditRiskChanges(patch) }));
    }
    setSyncedAt(new Date().toISOString());
    setSyncOpen(false);
  };

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: ReturnType<typeof complianceFormFields> & { complete?: boolean }
  ): TradingCounterParty => ({
    ...specificCounterparty,
    ...syncedRootFields,
    ...syncedCreditRiskLifecycle,
    progress: {
      ...specificCounterparty.progress,
      ...syncedCreditRiskProgressFields
    },
    complianceLifecycle: {
      ...specificCounterparty.complianceLifecycle,
      ...values
    }
  });

  const complianceAssignees = useAssignees("legal");

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...complianceFormFields(), comments })
  });

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData: complianceFormFields(),
    currentComment,
    currentProgress: specificCounterparty.progress?.compliance,
    canSetInProgress: ownsLifecycle(roles, "compliance"),
    buildEntity: (values, progress) => {
      const saved = buildCounterparty(values);
      // Keep any cross-lifecycle progress changes made by the payload builder.
      return {
        ...saved,
        progress: { ...saved.progress, compliance: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.includes("compliance");

  const canComplete =
    formData.kycCheck &&
    (specificCounterparty?.marketAccessLifecycle?.kycCheckOnMft === "needed"
      ? formData.kycCheckMft
      : true);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Compliance"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box
        sx={{
          paddingTop: 2,
          gap: 2,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {specificCounterparty.isMerger && specificCounterparty.id && (
          <MergerSetupPanel
            kind="counterparty"
            entityId={specificCounterparty.id}
            mergerDescription={specificCounterparty.mergerDescription}
            answers={specificCounterparty.marketAccessLifecycle}
          />
        )}
        <Box display="flex" gap={2} alignItems="flex-start">
          <TextField
            sx={{ flex: 1 }}
            size="medium"
            label="Catalyst ID / BvD id"
            value={catalystId}
            onChange={(e) => setCatalystId(e.target.value)}
            helperText={
              syncedAt
                ? `Last synced ${dayjs(syncedAt).format("LL")}`
                : "Compliance calls it BvD id, Risk calls it Catalyst ID."
            }
          />
          <Button
            variant="outlined"
            onClick={() => setSyncOpen(true)}
            disabled={!catalystId.trim()}
            sx={{ marginTop: 1 }}
          >
            Sync with database
          </Button>
        </Box>

        <Box display="flex" gap={2}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1 }}>
            <FormControl size="medium" required error={!!errors.kycCheck}>
              <InputLabel>KYC check</InputLabel>
              <Select
                label="KYC check"
                value={formData.kycCheck}
                onChange={(e) => {
                  handleChange("kycCheck", e.target.value);
                  handleChange("sanctionScreening", e.target.value);
                }}
              >
                <MenuItem value="initiated">
                  <Typography variant="body1" component="span">
                    Initiated
                  </Typography>
                </MenuItem>
                <MenuItem value="rejected">
                  <Typography variant="body1" component="span">
                    Rejected
                  </Typography>
                </MenuItem>
                <MenuItem value="approved">
                  <Typography variant="body1" component="span">
                    Approved
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Autocomplete
            sx={{ flex: 1, alignSelf: "center" }}
            options={complianceAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              complianceAssignees.data?.find(
                (a) => a.email === formData.kycCheckAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("kycCheckAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="KYC check + sanction screening assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {specificCounterparty.marketAccessLifecycle?.kycCheckOnMft ===
        "needed" ? (
          <Box display="flex" gap={2}>
            <FormControl
              size="medium"
              required
              error={!!errors.kycCheckMft}
              sx={{ flex: 1 }}
            >
              <InputLabel>KYC check on MFT</InputLabel>
              <Select
                label="KYC check on MFT"
                value={formData.kycCheckMft}
                onChange={(e) => handleChange("kycCheckMft", e.target.value)}
              >
                <MenuItem value="initiated">
                  <Typography variant="body1" component="span">
                    Initiated
                  </Typography>
                </MenuItem>
                <MenuItem value="rejected">
                  <Typography variant="body1" component="span">
                    Rejected
                  </Typography>
                </MenuItem>
                <MenuItem value="approved">
                  <Typography variant="body1" component="span">
                    Approved
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={complianceAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                complianceAssignees.data?.find(
                  (a) => a.email === formData.kycCheckMftAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("kycCheckMftAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="KYC check on MFT assignee"
                  size="medium"
                />
              )}
            />
          </Box>
        ) : null}

        <Box display="flex" gap={2}>
          <FormControl
            disabled
            size="medium"
            required
            error={!!errors.creditCheck}
            sx={{ flex: 1 }}
          >
            <InputLabel>
              Credit check - to be completed by Credit Risk
            </InputLabel>
            <Select
              label="Credit check - to be completed by Credit Risk"
              value={formData.creditCheck}
              onChange={(e) => handleChange("creditCheck", e.target.value)}
            >
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            disabled
            sx={{ flex: 1 }}
            options={[]}
            getOptionLabel={(option) => `${option.email}`}
            value={{ email: formData.creditCheckAssignee, name: "" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit check assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {/* Only shown once credit is approved: a rejected counterparty has no
            amount worth displaying, and its limit of 0 read as a real figure.
            For counterparties a warehouse limit of 0 *is* the rejection, so the
            two always travel together. Brokers differ, see their dialog. */}
        {formData.creditCheck === "approved" && (
          <Box display="flex" gap={2}>
            <TextField
              disabled
              sx={{ flex: 1 }}
              label="Credit amount - filled by Credit Risk"
              // No amount on an approved credit check means no limit. A number
              // input cannot render that, so switch to text and say so.
              type={creditIsUnlimited ? "text" : "number"}
              value={
                creditIsUnlimited ? "Unlimited" : (formData.creditAmount ?? "")
              }
              error={!!errors.creditAmount}
              helperText={errors.creditAmount}
              slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
            />
            <TextField
              disabled
              sx={{ flex: 1 }}
              label="Credit currency - filled by Credit Risk"
              value={formData.creditCurrency}
            />
          </Box>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />

        <WarehouseSyncDialog
          open={syncOpen}
          onClose={() => setSyncOpen(false)}
          catalystId={catalystId}
          entityType="counterparty"
          hubValues={hubSyncValues}
          onApply={handleApplySync}
        />
      </Box>
    </LifecycleDialog>
  );
};
